import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiHeart, FiStar, FiAward, FiCoffee, FiMapPin, FiTruck, FiKey,
  FiVideo, FiDollarSign, FiCamera, FiZap, FiUsers, FiArrowRight,
  FiClock, FiCheck, FiHelpCircle, FiChevronDown, FiPhone
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useReservation } from '../contexts/ReservationContext';
import './SeoLandingPage.css';

const ICONS = {
  FiHeart, FiStar, FiAward, FiCoffee, FiMapPin, FiTruck, FiKey,
  FiVideo, FiDollarSign, FiCamera, FiZap, FiUsers, FiClock, FiCheck
};

// Convertit le markdown léger (**gras**) en JSX
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const SeoLandingPage = ({ data }) => {
  const [openFaq, setOpenFaq] = useState(0);
  const { open: openReservation } = useReservation();

  const phoneNumber = '33661630113';
  const whatsappMessage = encodeURIComponent(
    `Bonjour, je souhaite obtenir un devis pour : ${data.h1}.`
  );
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${whatsappMessage}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://limousine-vip-paris.fr/" },
      { "@type": "ListItem", "position": 2, "name": data.badge, "item": data.canonical }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": data.schemaService?.name || data.h1,
    "description": data.description,
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://limousine-vip-paris.fr/#business",
      "name": "VIP Limousine Paris",
      "telephone": "+33661630113",
      "url": "https://limousine-vip-paris.fr/"
    },
    "areaServed": [
      { "@type": "City", "name": "Paris" },
      { "@type": "AdministrativeArea", "name": "Île-de-France" }
    ],
    "url": data.canonical,
    "image": `https://limousine-vip-paris.fr${data.hero.image}`
  };

  const faqSchema = data.faqs && data.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faqs.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a }
    }))
  } : null;

  const vehicleSchema = data.schemaVehicle
    ? { "@context": "https://schema.org", ...data.schemaVehicle }
    : null;

  const handleOpenForm = () => openReservation({ evenement: data.h1 });

  return (
    <article className="seo-page">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.description} />
        <link rel="canonical" href={data.canonical} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={data.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`https://limousine-vip-paris.fr${data.hero.image}`} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
        {vehicleSchema && (
          <script type="application/ld+json">{JSON.stringify(vehicleSchema)}</script>
        )}
      </Helmet>

      {/* HERO */}
      <header className="seo-hero">
        <div className="seo-hero-image">
          <img
            src={data.hero.image}
            alt={data.hero.alt}
            width="1600"
            height="1067"
            fetchpriority="high"
            loading="eager"
          />
          <div className="seo-hero-overlay" />
        </div>
        <div className="seo-hero-content container">
          <nav className="seo-breadcrumb" aria-label="Fil d'Ariane">
            <Link to="/">Accueil</Link>
            <span> › </span>
            <span>{data.badge}</span>
          </nav>
          <span className="seo-badge">{data.badge}</span>
          <h1 className="seo-h1">{data.h1}</h1>
          <p className="seo-subtitle">{data.subtitle}</p>
          <div className="seo-hero-cta">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary seo-cta-primary">
              <FaWhatsapp size={20} />
              <span>Devis sur WhatsApp</span>
            </a>
            <a href={`tel:+${phoneNumber}`} className="btn btn-outline seo-cta-secondary">
              <FiPhone size={18} />
              <span>06 61 63 01 13</span>
            </a>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="seo-section seo-intro section-padding">
        <div className="container">
          {data.intro.map((paragraph, i) => (
            <p key={i} className="seo-intro-para">{renderInline(paragraph)}</p>
          ))}
        </div>
      </section>

      {/* HIGHLIGHTS */}
      {data.highlights && (
        <section className="seo-section seo-highlights section-padding">
          <div className="container">
            <h2 className="seo-h2 text-center">
              Pourquoi choisir VIP Limousine pour
              <span className="title-accent"> {data.badge.toLowerCase()}</span>
            </h2>
            <div className="seo-highlights-grid">
              {data.highlights.map((h, i) => {
                const Icon = ICONS[h.icon] || FiStar;
                return (
                  <div key={i} className="seo-highlight-card">
                    <div className="seo-highlight-icon"><Icon size={24} /></div>
                    <h3>{h.title}</h3>
                    <p>{h.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CONTENT SECTIONS */}
      {data.sections && data.sections.map((sec, i) => (
        <section
          key={i}
          className={`seo-section seo-content ${sec.image ? 'has-image' : ''} ${i % 2 === 1 ? 'reverse' : ''} section-padding`}
        >
          <div className="container">
            <div className="seo-content-inner">
              {sec.image && (
                <div className="seo-content-image">
                  <img src={sec.image} alt={sec.imageAlt} loading="lazy" />
                </div>
              )}
              <div className="seo-content-text">
                <h2 className="seo-h2">{sec.heading}</h2>
                <p>{renderInline(sec.body)}</p>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* VEHICLES */}
      {data.vehicles && (
        <section className="seo-section seo-vehicles section-padding">
          <div className="container">
            <h2 className="seo-h2 text-center">
              Véhicules disponibles pour
              <span className="title-accent"> {data.badge.toLowerCase()}</span>
            </h2>
            <ul className="seo-vehicle-list">
              {data.vehicles.map((v, i) => (
                <li key={i} className="seo-vehicle-item">
                  <FiCheck className="seo-check" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      {data.faqs && data.faqs.length > 0 && (
        <section className="seo-section seo-faq section-padding">
          <div className="container">
            <h2 className="seo-h2 text-center">
              <FiHelpCircle style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Questions fréquentes
            </h2>
            <div className="seo-faq-list">
              {data.faqs.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <article
                    key={idx}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                    itemScope
                    itemType="https://schema.org/Question"
                  >
                    <button
                      className="seo-faq-question"
                      onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                      aria-expanded={isOpen}
                    >
                      <h3 itemProp="name">{item.q}</h3>
                      <FiChevronDown className="seo-faq-chevron" />
                    </button>
                    <div
                      className="seo-faq-answer"
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                      hidden={!isOpen}
                    >
                      <p itemProp="text">{item.a}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* RELATED PAGES (maillage interne SEO) */}
      {data.related && data.related.length > 0 && (
        <section className="seo-section seo-related section-padding">
          <div className="container">
            <h2 className="seo-h2 text-center">Nos autres services à Paris</h2>
            <div className="seo-related-grid">
              {data.related.map((r, i) => (
                <Link key={i} to={r.slug} className="seo-related-card">
                  <span>{r.title}</span>
                  <FiArrowRight />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section id="reservation-cta" className="seo-section seo-final-cta section-padding">
        <div className="container">
          <div className="seo-cta-box">
            <h2>Prêt à réserver votre {data.badge.toLowerCase()} en limousine&nbsp;?</h2>
            <p>Devis gratuit sous 1 heure, réservation 24/7, paiement sécurisé.</p>
            <div className="seo-cta-buttons">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <FaWhatsapp size={20} />
                <span>Devis WhatsApp</span>
              </a>
              <a href={`tel:+${phoneNumber}`} className="btn btn-outline">
                <FiPhone size={18} />
                <span>06 61 63 01 13</span>
              </a>
              <button type="button" className="btn btn-outline" onClick={handleOpenForm}>
                Formulaire en ligne
              </button>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default SeoLandingPage;
